import { omit } from "@common/helpers/objects";
import { createBillsTaskViewConfig } from "@common/helpers/close-template-task-view-config";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { FiltersTypes } from "@common/types/filters/general";
import { BillView, BillViewSchema } from "@common/types/views/billViews";
import { PersistableView } from "@common/types/views/views";
import { toDateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type { BillViewFilters } from "contexts/BillsViewContext";
import { parseTaskView, withBulkActions } from "../shared/taskTableView";

export function defaultBillsTaskView(): BillView {
    return BillViewSchema.parse(createBillsTaskViewConfig());
}

export function parseBillsTaskView(raw: object | null | undefined): BillView {
    return parseTaskView({
        schema: BillViewSchema,
        raw,
        createDefaultView: defaultBillsTaskView,
    });
}

/** Keys that identify a bills task's scope, not matching criteria. */
export const BILLS_TASK_SCOPE_KEYS = [
    "periodStart",
    "periodEnd",
    "entityIds",
    "search",
] as const;

export function omitBillsTaskScope(filters: FiltersTypes): FiltersTypes {
    return omit(filters, [...BILLS_TASK_SCOPE_KEYS]);
}

const TEMPLATE_APPLY_TIME_KEYS = [
    "periodStart",
    "periodEnd",
    "entityIds",
    "billIds",
] as const;

/**
 * Strips apply-time-only fields so a template stays workspace and period
 * agnostic: period, entity and pinned ids are injected when it is applied.
 */
export function serializeBillsTemplateView(
    view: BillView,
): CloseTemplateViewConfig {
    return {
        ...view,
        filters: omit(view.filters, [...TEMPLATE_APPLY_TIME_KEYS]),
    };
}

/**
 * Bill list filters (the bills page state) as task view filters: the date
 * range becomes the task's `periodStart` / `periodEnd`, "all time" becomes no
 * range, a bill id restriction (bill links) pins the task to those bills, and
 * filters the task has no use for are dropped.
 */
export function billPageFiltersToTaskFilters(
    filters: BillViewFilters | undefined,
): FiltersTypes {
    if (!filters) {
        return {};
    }
    const isAllTime = filters.start === undefined && filters.end !== undefined;
    const range = isAllTime
        ? {}
        : toDateOnlyDateRange({ start: filters.start, end: filters.end });
    const billIds = filters.billIds ?? filters.ids;
    return {
        ...omit(filters, [
            "ids",
            "billIds",
            "hasOpenAmount",
            "start",
            "end",
            "startDate",
            "endDate",
        ]),
        periodStart: filters.startDate ?? range.start,
        periodEnd: filters.endDate ?? range.end,
        ...(billIds?.length ? { billIds } : {}),
    };
}

interface BuildBulkBillsTaskViewParams {
    view: BillView;
    billIds: string[];
    entityIds: number[];
}

/**
 * View of a task created from selected bills: the bills table's current
 * columns and exactly the selected bills.
 */
export function buildBulkBillsTaskView({
    view,
    billIds,
    entityIds,
}: BuildBulkBillsTaskViewParams): PersistableView {
    return withBulkActions({ ...view, filters: { billIds, entityIds } });
}

interface BuildFilteredBillsTaskViewParams {
    view: BillView;
    filters: BillViewFilters | undefined;
}

/** View of a task created from "select all" of the bills matching the page filters. */
export function buildFilteredBillsTaskView({
    view,
    filters,
}: BuildFilteredBillsTaskViewParams): PersistableView {
    return withBulkActions({
        ...view,
        filters: billPageFiltersToTaskFilters(filters),
    });
}
